import React, { useState, useEffect } from 'react';
import { KeyRound, Loader2, Eye, EyeOff } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useToast } from '@/components/ui/use-toast';
import { updateUser } from '@/pages/parametrizacion/usuarios/Services/users.services';

/**
 * Modal para que un administrador establezca una nueva contraseña
 * a cualquier usuario del sistema, sin necesidad de conocer la actual.
 * Reutiliza `POST /user/update`, que ya hashea `contrasena` en el backend.
 */
const CambiarContrasenaModal = ({ isOpen, onClose, usuario, onSuccess }) => {
  const { toast } = useToast();
  const [nueva, setNueva] = useState('');
  const [confirmar, setConfirmar] = useState('');
  const [showNueva, setShowNueva] = useState(false);
  const [showConfirmar, setShowConfirmar] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isOpen) {
      setNueva('');
      setConfirmar('');
      setShowNueva(false);
      setShowConfirmar(false);
      setError('');
    }
  }, [isOpen, usuario]);

  const handleGuardar = async () => {
    if (nueva.length < 6) {
      setError('La contraseña debe tener al menos 6 caracteres');
      return;
    }
    if (nueva !== confirmar) {
      setError('Las contraseñas no coinciden');
      return;
    }
    setError('');
    setIsSaving(true);
    try {
      await updateUser(usuario._id, { contrasena: nueva });
      toast({ title: 'Éxito', description: `Contraseña actualizada para ${usuario.nombre}.` });
      onSuccess?.();
      onClose();
    } catch (err) {
      toast({ title: 'Error', description: err.message || 'No se pudo cambiar la contraseña.', variant: 'destructive' });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-md w-full">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-lg">
            <KeyRound className="h-5 w-5 text-primary" />
            Cambiar Contraseña — <span className="text-primary">{usuario?.nombre}</span>
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="space-y-1.5">
            <Label htmlFor="nueva-contrasena">
              Nueva contraseña <span className="text-destructive">*</span>
            </Label>
            <div className="relative">
              <Input
                id="nueva-contrasena"
                type={showNueva ? 'text' : 'password'}
                value={nueva}
                onChange={(e) => { setNueva(e.target.value); setError(''); }}
                placeholder="Nueva contraseña"
                className="pr-10"
                autoComplete="new-password"
              />
              <button
                type="button"
                className="absolute inset-y-0 right-2 flex items-center text-muted-foreground hover:text-foreground"
                onClick={() => setShowNueva((v) => !v)}
                aria-label={showNueva ? 'Ocultar contraseña' : 'Mostrar contraseña'}
              >
                {showNueva ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="confirmar-contrasena">
              Repetir contraseña <span className="text-destructive">*</span>
            </Label>
            <div className="relative">
              <Input
                id="confirmar-contrasena"
                type={showConfirmar ? 'text' : 'password'}
                value={confirmar}
                onChange={(e) => { setConfirmar(e.target.value); setError(''); }}
                placeholder="Repetir contraseña"
                className="pr-10"
                autoComplete="new-password"
              />
              <button
                type="button"
                className="absolute inset-y-0 right-2 flex items-center text-muted-foreground hover:text-foreground"
                onClick={() => setShowConfirmar((v) => !v)}
                aria-label={showConfirmar ? 'Ocultar contraseña' : 'Mostrar contraseña'}
              >
                {showConfirmar ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>

          {error && <p className="text-sm font-medium text-destructive">{error}</p>}
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" onClick={onClose} disabled={isSaving}>
            Cancelar
          </Button>
          <Button onClick={handleGuardar} disabled={isSaving}>
            {isSaving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
            Guardar contraseña
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CambiarContrasenaModal;
